import { useEffect, useId, useRef, useState } from "react";

import { INITIAL_NECKLACE_FONT } from "@components/ProductPage/mto/mtoEngraving";

/**
 * Renders text centered along an SVG path, with letter spacing
 * computed dynamically so the word always spans `targetWidth`.
 *
 * <CurvedText
 *   word="KARIN"
 *   path="M0 70.9785C168.738 107.595 261.124 106.699 444 62.9785"
 *   viewBox="0 0 445 178"
 *   targetWidth={138}
 * />
 */
export default function CurvedText({
  word = "",
  path,
  viewBox = "0 0 445 178",
  targetWidth = 300,
  fontSize = 30,
  dy = 0,
  className = "",
  pathId,
}) {
  const textRef = useRef(null);
  // The path and the gradient are referenced by id, which is resolved against the whole document:
  // two curved texts on the page (a cart thumbnail and the product preview) must not share them
  const uid = useId().replace(/:/g, "");
  const curveId = pathId ?? `curve-${uid}`;
  const gradientId = `goldGradient-${uid}`;
  const maskId = `goldMask-${uid}`;
  const chars = word.slice(0, 9).split("");
  const [dxValues, setDxValues] = useState(() => chars.map(() => 0));

  useEffect(() => {
    const textEl = textRef.current;
    if (!textEl || chars.length < 2) {
      setDxValues(chars.map(() => 0));
      return;
    }

    // Measure natural width with no spacing applied, then
    // distribute the remainder evenly between characters.
    const natural = textEl.getComputedTextLength ? textEl.getComputedTextLength() : 0;
    const extra = Math.max(0, targetWidth - natural);
    const dx = extra / (chars.length - 1);

    setDxValues(chars.map((_, i) => (i === 0 ? 0 : dx)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [word, targetWidth]);

  // On a tspan, a bounding-box gradient spans the whole <text>: measure each letter once the
  // spacing is applied so every letter gets the gradient over its own box
  const [charBoxes, setCharBoxes] = useState([]);

  useEffect(() => {
    const textEl = textRef.current;
    if (!textEl?.getExtentOfChar) return;
    try {
      setCharBoxes(
        chars.map((_, i) => {
          const { x, y, width, height } = textEl.getExtentOfChar(i);
          return width && height ? { x, y, width, height } : null;
        })
      );
    } catch {
      // Text not rendered yet (e.g. hidden): keep the whole-word gradient
      setCharBoxes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dxValues, dy, fontSize, path, viewBox]);

  // The gradient-filled letters are painted as rects behind a mask cut out by the text: WebKit
  // clips a glyph rotated along a path to its un-rotated box when the glyph itself carries a
  // gradient fill, which sliced the letters at the ends of the curve (a flat fill renders fine)
  const [vbX, vbY, vbWidth, vbHeight] = viewBox.split(/[\s,]+/).map(Number);

  return (
    <svg
      className="w-full h-full absolute top-0 left-0"
      viewBox={viewBox}
      fill="none"
      role="img"
      aria-label={word}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {chars.map((char, index) => {
          const box = charBoxes[index];
          return (
            <linearGradient
              key={`gradient-${index}-${char.toLowerCase()}`}
              id={`${gradientId}-${index}`}
              x1="0.25"
              y1="0.933"
              x2="0.75"
              y2="0.067"
              // Maps the unit square onto the letter's box, same as objectBoundingBox on the letter alone
              gradientUnits={box ? "userSpaceOnUse" : "objectBoundingBox"}
              gradientTransform={
                box ? `matrix(${box.width} 0 0 ${box.height} ${box.x} ${box.y})` : undefined
              }
            >
              <stop offset="0%" stopColor="#F9E1B5" />
              <stop offset="100%" stopColor="#E2B86E" />
            </linearGradient>
          );
        })}
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x={vbX}
          y={vbY}
          width={vbWidth}
          height={vbHeight}
        >
          {/* Safari ignores dominant-baseline on text laid out along a path (and every browser
              ignores dy on <textPath> itself), so the hanging baseline is reproduced by hand on the
              <text>: Chrome shifts a hanging baseline down by 0.8 x ascent, InitialNeck's ascent
              being 0.8em */}
          <text ref={textRef} width={targetWidth} dy={fontSize * 0.64 + dy} fill="#fff">
            <textPath
              href={`#${curveId}`}
              startOffset="50%"
              textAnchor="middle"
              style={{
                fontFamily: INITIAL_NECKLACE_FONT.font_family,
                fontSize: `${fontSize}px`,
                lineHeight: 1,
              }}
              className={className}
            >
              {chars.map((char, index) => (
                <tspan
                  key={`letter-${index}-${char.toLowerCase()}`}
                  dx={dxValues[index] || undefined}
                >
                  {char}
                </tspan>
              ))}
            </textPath>
          </text>
        </mask>
      </defs>
      <path id={curveId} d={path} stroke="transparent" fill="none" />
      <g mask={`url(#${maskId})`}>
        {charBoxes.some(Boolean) ? (
          charBoxes.map((box, index) =>
            box ? (
              <rect
                key={`letter-fill-${index}-${chars[index]?.toLowerCase()}`}
                x={box.x}
                y={box.y}
                width={box.width}
                height={box.height}
                fill={`url(#${gradientId}-${index})`}
              />
            ) : null
          )
        ) : (
          // Until the letters are measured (first paint, server render) the whole word shares one gradient
          <rect x={vbX} y={vbY} width={vbWidth} height={vbHeight} fill={`url(#${gradientId}-0)`} />
        )}
      </g>
    </svg>
  );
}
